/** 后台用户列表中展示的一条有效服务授权。 */
export type AdminUserServiceGrant = {
  name: string;
  expiresAt: string | null;
  productId: string;
  organizationId: string;
  grantType: string;
  scopeType: string;
  organizationName: string;
};

type SubscriptionLike = {
  id?: string;
  status?: string;
  userId?: string;
  productId?: string;
  organizationId?: string;
  ownerType?: string;
  grantType?: string;
  expiresAt?: string | null;
  orderId?: string;
};

type SeatLike = {
  status?: string;
  userId?: string;
  productId?: string;
  organizationId?: string;
  endsAt?: string | null;
  subscriptionId?: string;
};

type OrderLike = {
  id?: string;
  userId?: string;
  productSnapshot?: {name?: string};
};

type ProductLike = {
  id?: string;
  name?: string;
};

type OrganizationLike = {
  id?: string;
  name?: string;
  organizationType?: string;
};

function grantKey(item: AdminUserServiceGrant): string {
  return `${item.productId}|${item.scopeType}|${item.organizationId}|${item.grantType}`;
}

function upsertGrant(
  accessByUser: Map<string, Map<string, AdminUserServiceGrant>>,
  userId: string,
  item: AdminUserServiceGrant,
): void {
  const ownerId = String(userId || '').trim();
  if (!ownerId) return;
  const entries = accessByUser.get(ownerId) ?? new Map<string, AdminUserServiceGrant>();
  const key = grantKey(item);
  const existing = entries.get(key);
  if (
    !existing
    || existing.expiresAt === null
    || (item.expiresAt !== null && Date.parse(item.expiresAt) > Date.parse(existing.expiresAt || ''))
  ) {
    entries.set(key, item);
  }
  accessByUser.set(ownerId, entries);
}

function isSeatCurrentlyActive(seat: SeatLike, nowMs: number): boolean {
  if (String(seat.status || '') !== 'active') return false;
  const endsAt = seat.endsAt ? Date.parse(String(seat.endsAt)) : NaN;
  return !Number.isFinite(endsAt) || endsAt > nowMs;
}

/**
 * 汇总后台用户管理页应展示的服务授权：个人订阅/代开通 + 团队席位。
 * 企业订阅池记在购买人身上，不等于成员已获授权，故不按订阅 userId 展示。
 */
export function buildAdminUserServiceAccessByUser(input: {
  subscriptions: SubscriptionLike[];
  seats?: SeatLike[];
  orders?: OrderLike[];
  products?: ProductLike[];
  organizations?: OrganizationLike[];
  nowMs?: number;
}): Map<string, Map<string, AdminUserServiceGrant>> {
  const accessByUser = new Map<string, Map<string, AdminUserServiceGrant>>();
  const ordersById = new Map((input.orders ?? []).map(order => [String(order.id || ''), order]));
  const productsById = new Map((input.products ?? []).map(product => [String(product.id || ''), product]));
  const organizationsById = new Map((input.organizations ?? []).map(organization => [String(organization.id || ''), organization]));
  const nowMs = input.nowMs ?? Date.now();

  for (const subscription of input.subscriptions) {
    if (String(subscription.status || '') !== 'active') continue;
    if (String(subscription.ownerType || '') === 'enterprise') continue;
    const order = ordersById.get(String(subscription.orderId || ''));
    const ownerId = String(subscription.userId || order?.userId || '');
    const product = productsById.get(String(subscription.productId || ''));
    const organization = organizationsById.get(String(subscription.organizationId || ''));
    const name = String(product?.name || order?.productSnapshot?.name || '服务功能');
    const expiresAt = subscription.expiresAt ? String(subscription.expiresAt) : null;
    const grantType = String(subscription.grantType || 'purchased');
    const scopeType = organization?.organizationType === 'team' ? 'enterprise' : 'personal';
    upsertGrant(accessByUser, ownerId, {
      name,
      expiresAt,
      productId: String(subscription.productId || ''),
      organizationId: String(subscription.organizationId || ''),
      grantType,
      scopeType,
      organizationName: String(organization?.name || '未知空间'),
    });
  }

  const subscriptions = input.subscriptions;
  for (const seat of input.seats ?? []) {
    if (!isSeatCurrentlyActive(seat, nowMs)) continue;
    const product = productsById.get(String(seat.productId || ''));
    const organization = organizationsById.get(String(seat.organizationId || ''));
    const covering = subscriptions.find(item =>
      String(item.id || '') === String(seat.subscriptionId || '')
      || (
        String(item.organizationId || '') === String(seat.organizationId || '')
        && String(item.productId || '') === String(seat.productId || '')
        && String(item.ownerType || '') === 'enterprise'
        && String(item.status || '') === 'active'
      ),
    );
    upsertGrant(accessByUser, String(seat.userId || ''), {
      name: String(product?.name || '服务功能'),
      expiresAt: seat.endsAt ? String(seat.endsAt) : (covering?.expiresAt ? String(covering.expiresAt) : null),
      productId: String(seat.productId || ''),
      organizationId: String(seat.organizationId || ''),
      grantType: 'seat_assigned',
      scopeType: 'enterprise',
      organizationName: String(organization?.name || '未知空间'),
    });
  }

  return accessByUser;
}
